// Ported from the Software (Piping/ParentTabsTest/MainTabs/Spec/specRow.js) so
// the web reads spec rows exactly the way the Software writes them. Keep the
// two in step: a row normalised differently here would show up as a duplicate,
// or not merge with its SQLite counterpart.

// From the Software's specRules.js - the only thing specRow needs from it, and
// not worth porting that 1000-line Spec Helper module for.
export function getFamilyFromComponent(component) {
  if (!component) return "";

  const c = String(component).toLowerCase();

  if (c === "pipe") return "Pipes";
  if (c.includes("valve")) return "Valves";
  if (c.startsWith("elbow")) return "Elbows";
  if (c === "blind flange") return "Blind Flanges";
  if (c === "flange" || c === "orifice flange") return "Flanges";
  if (c === "spectacle blind") return "Spectacle Blinds";
  if (c.startsWith("tee") || c === "reducing tee" || c === "cross") return "Tees";
  if (c.startsWith("reducer")) return "Reducers";
  if (c === "cap") return "Caps";
  if (c === "coupling" || c === "half coupling") return "Couplings";
  if (c === "union") return "Unions";
  if (c.endsWith("olet")) return "Olets";
  if (c.includes("nipple")) return "Nipples";
  if (c.startsWith("strainer")) return "Strainers";
  if (c === "expansion joint" || c === "flexible hose") return "Expansion Joints";

  return "";
}


// ── Canonical spec-row helpers ─────────────────────────────────────────────
//
// A spec row is carried in three naming conventions at once:
//   * Legacy main-grid names: Components, Size, Schedule/Rating, Ends, Additional Notes
//   * Spec Helper names:      Component, Size From / Size To, Rating / Sch. / Thk.,
//                             End / Face Finish, Special Requirements
//   * User-entered fields:    "Family Name", "Description String", "Est Table Name"
//
// These helpers are the single source of truth for row normalization.

export function normalizeSpecRowValue(value) {
  return String(value ?? "").trim();
}

export function getSpecRowComponent(row) {
  return row?.Components || row?.Component || "";
}

export function getSpecRowSchedule(row) {
  return row?.["Schedule/Rating"] || row?.["Rating / Sch. / Thk."] || "";
}

export function getSpecRowEnds(row) {
  return row?.Ends || row?.["End / Face Finish"] || "";
}

export function getSpecRowNotes(row) {
  return row?.["Additional Notes"] || row?.["Special Requirements"] || "-";
}

export function getSpecRowRev(row) {
  return row?.Rev == null || row?.Rev === "" ? "0" : String(row.Rev);
}

export function getSpecRowSize(row) {
  if (row?.Size) return row.Size;

  const sizeFrom = row?.["Size From"] || row?.sizeFrom || "";
  const sizeTo = row?.["Size To"] || row?.sizeTo || "";

  if (sizeFrom && sizeTo && sizeFrom !== sizeTo) {
    return `${sizeFrom} -- ${sizeTo}`;
  }

  return sizeFrom || sizeTo || "";
}

export function getSpecRowFamilyName(row) {
  return row?.["Family Name"] || row?.Family_Name || row?.familyName || "";
}

export function getSpecRowDescriptionString(row) {
  return (
    row?.["Description String"] ||
    row?.Description_String ||
    row?.descriptionString ||
    ""
  );
}

export function getSpecRowEstTableName(row) {
  return row?.["Est Table Name"] || row?.Est_Table_Name || row?.estTableName || "";
}

export function getSpecRowPartsLibTab(row) {
  return (
    row?.Parts_Lib_Tab ||
    row?.["Parts Lib Tab"] ||
    getFamilyFromComponent(getSpecRowComponent(row))
  );
}

export function buildSpecRowIdentity(row) {
  if (!row) return "";

  if (row.id) return `id:${row.id}`;

  return [
    "spec-row",
    normalizeSpecRowValue(row.Spec),
    normalizeSpecRowValue(getSpecRowRev(row)),
    normalizeSpecRowValue(getSpecRowComponent(row)),
    normalizeSpecRowValue(getSpecRowSize(row)),
    normalizeSpecRowValue(getSpecRowSchedule(row)),
    normalizeSpecRowValue(getSpecRowEnds(row)),
    normalizeSpecRowValue(row.Material),
    normalizeSpecRowValue(getSpecRowNotes(row)),
  ].join("|");
}

export function buildSpecRowSignature(row) {
  return JSON.stringify({
    id: row?.id || "",
    Spec: row?.Spec || "",
    Rev: getSpecRowRev(row),
    Components: getSpecRowComponent(row),
    Size: getSpecRowSize(row),
    "Schedule/Rating": getSpecRowSchedule(row),
    Ends: getSpecRowEnds(row),
    Material: row?.Material || "",
    "Additional Notes": getSpecRowNotes(row),
    "Family Name": getSpecRowFamilyName(row),
    "Description String": getSpecRowDescriptionString(row),
    "Est Table Name": getSpecRowEstTableName(row),
    Parts_Lib_Tab: getSpecRowPartsLibTab(row),
  });
}

export function normalizeSpecRow(row, clientName = "DEMO") {
  const safeClientName = String(clientName || "DEMO").trim() || "DEMO";
  const component = getSpecRowComponent(row);
  const schedule = getSpecRowSchedule(row);
  const ends = getSpecRowEnds(row);
  const notes = getSpecRowNotes(row);
  const size = getSpecRowSize(row);

  const familyName = getSpecRowFamilyName(row);
  const descriptionString = getSpecRowDescriptionString(row);
  const estTableName = getSpecRowEstTableName(row);
  const partsLibTab = getSpecRowPartsLibTab(row);

  return {
    ...row,

    Rev: getSpecRowRev(row),
    clientName:
      row?.clientName == null || row?.clientName === ""
        ? JSON.stringify([safeClientName])
        : row.clientName,

    // Legacy names used by the main spec grid.
    Components: component,
    Size: size,
    "Schedule/Rating": schedule,
    Ends: ends,
    "Additional Notes": notes,

    // New Spec Helper names kept for the dialog.
    Component: row?.Component || component,
    "Rating / Sch. / Thk.": row?.["Rating / Sch. / Thk."] || schedule,
    "End / Face Finish": row?.["End / Face Finish"] || ends,
    "Special Requirements": row?.["Special Requirements"] || notes,

    // Automated export/display columns.
    Parts_Lib_Tab: partsLibTab,
    "Parts Lib Tab": partsLibTab,

    // User-entered estimation/export columns.
    "Family Name": familyName,
    "Description String": descriptionString,
    "Est Table Name": estTableName,

    // Backward-compatible aliases.
    Family_Name: familyName,
    Description_String: descriptionString,
    Est_Table_Name: estTableName,
    familyName,
    descriptionString,
    estTableName,
  };
}

export function mergeSpecRowsByIdentity(rows, clientName = "DEMO") {
  const map = new Map();

  (Array.isArray(rows) ? rows : []).forEach((row) => {
    const normalized = normalizeSpecRow(row, clientName);
    const key = buildSpecRowIdentity(normalized);

    if (!key) return;

    map.set(key, normalized);
  });

  return Array.from(map.values());
}
